import { QUESTS, QUEST_REP_DEFAULT, questRep, type QuestDef, type QuestState } from "./quests.js";
import { getTrader, tierFor, TRADERS, type OfferStanding, type TraderDef } from "./traders.js";

/**
 * How well one trader knows you.
 *
 * REPUTATION IS DERIVED, NEVER STORED:
 *
 *     rep(trader) = Σ questRep(q) over that trader's DONE quests
 *                 + floor(turnover[trader.id] / LOYALTY.eurPerRep)
 *
 * The quest half is already on the profile, so retuning `rewardRep` re-derives
 * every account on its next read with no migration. Turnover — euros across
 * the counter in EITHER direction (`buyPrice` and `sellPriceDeep` alike) — is
 * the only persisted state. Quest turn-ins and bench repairs add nothing; the
 * server simply does not call `addTurnover` for them.
 *
 * Pure and total. Imports both `traders.ts` and `quests.ts` (quests already
 * imports traders), which is why `validateShelfUnlocks` lives here.
 */

/**
 * The two tuning numbers. `eurPerRep` 50 puts tier 1 (50 rep) at 2 500 € of
 * pure custom and the top rung (400) at 20 000 € — turnover is the slow road,
 * errands the fast one. `questDefault` is `QUEST_REP_DEFAULT` from `quests.ts`.
 */
export const LOYALTY = {
  questDefault: QUEST_REP_DEFAULT,
  /** Euro across one counter per point of reputation. */
  eurPerRep: 50,
} as const;

/**
 * The only persisted loyalty state: whole euros across each counter, keyed by
 * trader id. No key = never traded = 0; a fresh account is `{}`.
 */
export interface LoyaltyState {
  /** trader id → whole Euro, non-negative. Absent means 0. */
  turnover: Record<string, number>;
}

/** A brand-new account: a stranger at every counter in the valley. */
export function createLoyalty(): LoyaltyState {
  return { turnover: {} };
}

/**
 * Turns anything into a usable `LoyaltyState`; never throws. Non-objects yield
 * an empty state, unknown trader ids are dropped (removing a trader is a
 * content edit, not a migration), non-finite or negative values are dropped
 * rather than clamped (absent and 0 mean the same), everything else is
 * floored. Own-property reads only — the keys come off a database blob.
 */
export function sanitizeLoyalty(x: unknown): LoyaltyState {
  const out = createLoyalty();
  if (!isRecord(x)) return out;

  const rawTurnover = own(x, "turnover");
  if (!isRecord(rawTurnover)) return out;

  for (const id of Object.keys(rawTurnover)) {
    if (getTrader(id) === null) continue;
    const eur = own(rawTurnover, id);
    if (typeof eur !== "number" || !Number.isFinite(eur) || eur <= 0) continue;
    out.turnover[id] = Math.floor(eur);
  }
  return out;
}

/**
 * `state` with `eur` more euros across `traderId`'s counter. Pure; a
 * non-finite or non-positive amount returns the state unchanged rather than
 * throwing — the callers have already moved items. The trader id is not
 * checked here: `sanitizeLoyalty` drops unknown counters on the next load.
 */
export function addTurnover(state: LoyaltyState, traderId: string, eur: number): LoyaltyState {
  if (typeof eur !== "number" || !Number.isFinite(eur) || eur <= 0) return state;
  const prev: Record<string, number> =
    isRecord(state) && isRecord(state.turnover) ? state.turnover : {};
  const had = own(prev, traderId);
  const base = typeof had === "number" && Number.isFinite(had) && had > 0 ? Math.floor(had) : 0;
  return { turnover: { ...prev, [traderId]: base + Math.floor(eur) } };
}

/**
 * `traderId`'s reputation for this profile, in whole points. Quests are
 * counted off the CATALOG (this trader's entries, asked about the states), so
 * a stale, foreign or duplicated state pays nothing; turnover is one floored
 * division. An unknown trader is 0 — the id arrives off the wire.
 */
export function traderRep(
  loyalty: LoyaltyState,
  quests: readonly QuestState[],
  traderId: string,
  catalog: readonly QuestDef[] = QUESTS,
): number {
  if (getTrader(traderId) === null) return 0;

  const done = new Set<string>();
  for (const s of quests ?? []) {
    if (isRecord(s) && typeof s.id === "string" && s.done === true) done.add(s.id);
  }

  let rep = 0;
  for (const q of catalog) {
    if (q.traderId !== traderId) continue;
    if (!done.has(q.id)) continue;
    rep += questRep(q);
  }

  const turnover: Record<string, number> =
    isRecord(loyalty) && isRecord(loyalty.turnover) ? loyalty.turnover : {};
  const eur = own(turnover, traderId);
  if (typeof eur === "number" && Number.isFinite(eur) && eur > 0) {
    rep += Math.floor(eur / LOYALTY.eurPerRep);
  }

  return rep;
}

/** Which of the four rungs that rep lands on. The number the shelf is drawn
 *  against, and the one `OfferStanding.tier` carries. */
export function traderTier(
  loyalty: LoyaltyState,
  quests: readonly QuestState[],
  traderId: string,
  catalog: readonly QuestDef[] = QUESTS,
): number {
  return tierFor(traderRep(loyalty, quests, traderId, catalog));
}

/**
 * Everything one counter checks its shelf against, built once so the client
 * greying rows and the server refusing a purchase derive the same facts.
 * `level` is passed in because both callers already hold it. `isQuestDone`
 * closes over the states; an offer may only name its own trader's quest.
 */
export function traderStanding(
  loyalty: LoyaltyState,
  quests: readonly QuestState[],
  level: number,
  traderId: string,
  catalog: readonly QuestDef[] = QUESTS,
): OfferStanding {
  const done = new Set<string>();
  for (const s of quests ?? []) {
    if (isRecord(s) && typeof s.id === "string" && s.done === true) done.add(s.id);
  }
  return {
    level: typeof level === "number" && Number.isFinite(level) && level > 0 ? level : 1,
    tier: traderTier(loyalty, quests, traderId, catalog),
    isQuestDone: (questId: string) => done.has(questId),
  };
}

/**
 * Structural check of the shelves' `afterQuest` gates, in the shape of
 * `validateTraders`: an empty result means the content is sound. Run as a unit
 * test and a boot assertion; lives here because `quests.ts` imports
 * `traders.ts`. Two questions per gate: does the id resolve to a quest (a typo
 * is a line nobody can ever buy), and does that quest belong to the shelf's
 * own trader. The parameters let a test feed a synthetic shelf.
 */
export function validateShelfUnlocks(
  traders: readonly TraderDef[] = TRADERS,
  catalog: readonly QuestDef[] = QUESTS,
): string[] {
  const problems: string[] = [];
  const byId = new Map<string, QuestDef>(catalog.map((q) => [q.id, q]));

  for (const t of traders) {
    for (const offer of t.offers) {
      const questId = offer.afterQuest;
      if (questId === undefined || questId.length === 0) continue;
      const quest = byId.get(questId);
      if (quest === undefined) {
        problems.push(`trader "${t.id}" gates "${offer.defId}" behind unknown quest "${questId}"`);
        continue;
      }
      if (quest.traderId !== t.id) {
        problems.push(
          `trader "${t.id}" gates "${offer.defId}" behind quest "${questId}", which belongs to trader "${quest.traderId}"`,
        );
      }
    }
  }

  return problems;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** Own-property read off an untrusted object, so `turnover["toString"]` is
 *  nothing rather than a function — the guard `sanitizeProgress` spells out. */
function own(rec: Record<string, unknown>, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(rec, key) ? rec[key] : undefined;
}
